import { Renderable, Toast, toast, ToastOptions, ValueOrFunction } from 'react-hot-toast';

/**
 * Custom hook for managing toast notifications.
 * Provides methods for displaying success, error, and loading toasts,
 * as well as dismissing toasts and generating toast IDs.
 * 
 * @returns An object containing toast utility functions
 */
export const useToast = () => {
  const defaultOptions: ToastOptions = {
    position: 'bottom-right',
    style: { padding: 'var(--padding)' }
  };

  /**
   * Displays a success toast notification.
   * 
   * @param {ValueOrFunction<Renderable, Toast>} message - The content to display in the toast
   * @param {ToastOptions} [options] - Optional configuration options for the toast
   */
  const toastSuccess = (message: ValueOrFunction<Renderable, Toast>, options?: ToastOptions) => {
    toast.success(message, { ...defaultOptions, ...options });
  };

  /**
   * Displays an error toast notification.
   * Error toasts have a longer duration (10 seconds) by default.
   * 
   * @param {ValueOrFunction<Renderable, Toast>} message - The content to display in the toast
   * @param {string} [toastId] - Optional ID for the toast, useful for updating or dismissing it later
   */
  const toastError = (message: ValueOrFunction<Renderable, Toast>, toastId?: string) => {
    const options: ToastOptions = {
      ...defaultOptions,
      duration: 10000,
      ...(toastId && { id: toastId })
    };
    toast.error(message, options);
  };

  /**
   * Displays a loading toast notification.
   * Loading toasts persist until explicitly dismissed.
   * 
   * @param {ValueOrFunction<Renderable, Toast>} message - The content to display in the toast
   * @param {string} [toastId] - Optional ID for the toast, useful for updating or dismissing it later
   * @returns {string} The ID of the created toast
   */
  const toastLoading = (message: ValueOrFunction<Renderable, Toast>, toastId?: string): string => {
    return toast.loading(message, { ...defaultOptions, ...(toastId && { id: toastId }) });
  };

  /**
   * Dismisses a toast notification by its ID.
   * 
   * @param {string} toastId - The ID of the toast to dismiss
   */
  const toastDismiss = (toastId: string) => {
    toast.dismiss(toastId);
  };

  /**
   * Generates a toast ID for a command log.
   * Creates a consistent ID format by combining 'commandLogId' with the provided ID.
   * 
   * @param {number} commandLogId - The command log ID to generate a toast ID for
   * @returns {string} The generated toast ID
   */
  const getCommandLogToastId = (commandLogId: number) => {
    return ['commandLogId', commandLogId].join('-');
  };

  return { toastSuccess, toastError, toastLoading, toastDismiss, getCommandLogToastId };
};
